import { ref } from 'vue'
import apiInstance from '@/utils/api'

// ===== ORDER MODES INTERFACES =====
export interface TimeSlot {
  startTime: string
  endTime: string
}

export interface Mode1Settings {
  enabled: boolean
  label: string
  minimumOrderAmount: number
  priceLevel: string
  receptionAccount: string
  diningChoice: string
  startupMessage: boolean
  startupMessageText: string
  activateHeuchelbergMode: boolean
  showEmailField: boolean
  hideMobileField: boolean
  showNameOnBasket: boolean
  enablePayment: boolean
  waitingTimeAlgorithm: string
  waitingTimeAlgorithmValue: number
  minMinutesBetweenOrders: number
  activateTipOption: boolean
  orderConfirmationMessage: string
  modeAppURL: string
}

export interface Mode2Settings {
  enabled: boolean
  label: string
  minimumOrderAmount: number
  priceLevel: string
  locationIdentifier: string
  showEmailField: boolean
  showNameOnBasket: boolean
  showWishesComments: boolean
  enablePayment: boolean
  rateLimitingEnabled: boolean
  maxAmountPerOrder: number
  timeBetweenOrders: number
  maxOrderPerDay: number
  qrPaymentEnabled: boolean
  bookAsTakeaway: boolean
  activateTipOption: boolean
  payAtTableOption: boolean
}

export interface OrderModesData {
  mode1: Mode1Settings
  mode2: Mode2Settings
}

export interface ReceptionAccountOption {
  id: number
  name?: string
  email?: string
  status?: number
  accountName?: string
  [key: string]: any
}

export interface PriceLevelOption {
  value: number
  title: string
}

const initializeOrderModesData = (): OrderModesData => {
  return {
    mode1: {
      enabled: false,
      label: 'Self Service',
      minimumOrderAmount: 0,
      priceLevel: 'standard',
      receptionAccount: '',
      diningChoice: '3',
      startupMessage: false,
      startupMessageText: '',
      activateHeuchelbergMode: false,
      showEmailField: true,
      hideMobileField: false,
      showNameOnBasket: false,
      enablePayment: false,
      waitingTimeAlgorithm: 'default',
      waitingTimeAlgorithmValue: 0,
      minMinutesBetweenOrders: 0,
      activateTipOption: false,
      orderConfirmationMessage: '',
    },
    mode2: {
      enabled: false,
      label: 'Self Ordering',
      minimumOrderAmount: 0,
      priceLevel: 'standard',
      locationIdentifier: '',
      showEmailField: true,
      showNameOnBasket: false,
      showWishesComments: false,
      enablePayment: false,
      rateLimitingEnabled: false,
      maxAmountPerOrder: 0,
      timeBetweenOrders: 0,
      maxOrderPerDay: 0,
      qrPaymentEnabled: false,
      bookAsTakeaway: false,
      activateTipOption: false,
      payAtTableOption: false,
    },
  }
}

// ===== COMPOSABLE FUNCTION =====
export const useOrderModes = () => {
  // State
  const orderModes = ref<OrderModesData>(initializeOrderModesData())
  const receptionAccounts = ref<ReceptionAccountOption[]>([])
  const priceLevels = ref<PriceLevelOption[]>([])
  let isLoadingDropdownData = false
  let dropdownDataLoaded = false

  // Update methods
  const updateOrderModes = (updates: Partial<OrderModesData>) => {
    orderModes.value = { ...orderModes.value, ...updates }
  }

  const updateMode1 = (data: Partial<Mode1Settings>) => {
    orderModes.value.mode1 = { ...orderModes.value.mode1, ...data }
  }

  const updateMode2 = (data: Partial<Mode2Settings>) => {
    orderModes.value.mode2 = { ...orderModes.value.mode2, ...data }
  }

  // Dropdown data methods
  const loadReceptionAccounts = async (id: string): Promise<void> => {
    try {
      const response = await apiInstance.get(`/restaurant/${id}/reception`)
      const data = response.data

      if (data.error === false || response.status === 200) {
        receptionAccounts.value = data.data || []
      } else {
        console.warn('⚠️ Could not load reception accounts')
        receptionAccounts.value = []
      }
    } catch (e) {
      console.warn('⚠️ Error loading reception accounts:', (e as Error).message)
      receptionAccounts.value = []
    }
  }

  const loadPriceLevels = async (id: string): Promise<void> => {
    try {
      const response = await apiInstance.get(`/restaurant/${id}/price-levels`)
      const data = response.data

      if (data.error === false || response.status === 200) {
        priceLevels.value = data.data || []
      } else {
        console.warn('⚠️ Could not load price levels')
        priceLevels.value = []
      }
    } catch (e) {
      console.warn('⚠️ Error loading price levels:', (e as Error).message)
      priceLevels.value = []
    }
  }

  const loadOrderModesDropdownData = async (id: string): Promise<void> => {
    if (isLoadingDropdownData || dropdownDataLoaded) {
      return
    }
    isLoadingDropdownData = true
    try {
      await loadPriceLevels(id)
      dropdownDataLoaded = true
    } finally {
      isLoadingDropdownData = false
    }
  }

  // Order mode settings methods
  const loadOrderModeSettings = async (id: string): Promise<any> => {
    try {
      const response = await apiInstance.get(`/restaurant/${id}/order-mode-settings`)
      const data = response.data

      if (data.error === false || response.status === 200) {
        return data.data || null
      } else {
        console.warn('⚠️ Could not load order mode settings')
        return null
      }
    } catch (e) {
      console.warn('⚠️ Error loading order mode settings:', (e as Error).message)
      return null
    }
  }

  const saveOrderModeSettings = async (id: string, orderOptions: any): Promise<boolean> => {
    try {
      const response = await apiInstance.put(`/restaurant/${id}/order-mode-settings`, {
        order_options: orderOptions,
      })
      const data = response.data

      if (data.error === false || response.status === 200) {
        return true
      } else {
        const errorMessage = data.message || 'Failed to save order mode settings'
        const error: any = new Error(errorMessage)
        if (data.errors && typeof data.errors === 'object') {
          error.fieldErrors = data.errors
        }
        throw error
      }
    } catch (e: any) {
      console.error('⚠️ Error saving order mode settings:', e)

      if (e.response?.status === 422 && e.response?.data?.errors) {
        const error: any = new Error(e.response.data.message || 'Validation failed. Please check the form for errors.')
        error.fieldErrors = e.response.data.errors
        throw error
      }

      const error: any = new Error(
        e.response?.data?.message || e.message || 'Network error while saving order mode settings'
      )
      if (e.response?.data?.errors && typeof e.response.data.errors === 'object') {
        error.fieldErrors = e.response.data.errors
      }
      throw error
    }
  }

  // Reset state
  const reset = () => {
    orderModes.value = initializeOrderModesData()
    receptionAccounts.value = []
    priceLevels.value = []
    isLoadingDropdownData = false
    dropdownDataLoaded = false
  }

  return {
    // State
    orderModes,
    receptionAccounts,
    priceLevels,

    // Update methods
    updateOrderModes,
    updateMode1,
    updateMode2,

    // Dropdown data methods
    loadReceptionAccounts,
    loadPriceLevels,
    loadOrderModesDropdownData,

    // Order mode settings methods
    loadOrderModeSettings,
    saveOrderModeSettings,

    // Reset
    reset,
  }
}
